import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { _api_add_friend, _api_get_friends_list } from "src/DAL/Chat/Chat";
import { useSnackbar } from "notistack";
import GymMembers from "./GymMembers";
import { gr } from "src/design-system/tokens";
import GrPage from "src/design-system/components/GrPage";
import GrLoader from "src/design-system/components/GrLoader";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrSearch from "src/design-system/components/GrSearch";
import GrTabs from "src/design-system/components/GrTabs";

const loader = <GrLoader sx={{ py: gr.space[12] }} />;

function FriendList() {
  const observer = useRef();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(state?.tabValue || 0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [reciveCount, setReciveCount] = useState(0);
  const [sendCount, setSendCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadMorePath, setLoadMorePath] = useState("");
  const [searchText, setSearchText] = useState("");
  const [friends, setFriends] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState([]);
  const [partnerList, setPartnerList] = useState([]);
  const load_More_Path = "api/friend_request/list_friends?page=0&limit=20";

  const get_friends_list = async (path, search, loading) => {
    let type = "";

    if (tabValue == 0) {
      type = "accepted";
    } else if (tabValue == 1) {
      type = "non_friend";
    } else if (tabValue == 2) {
      type = "sent_request";
    } else if (tabValue == 3) {
      type = "received_request";
    } else if (tabValue === 4) {
      type = "accountability_partner_list";
    }

    if (!loading) {
      setIsLoading(true);
      path = load_More_Path;
    }
    const result = await _api_get_friends_list(path, search, type);
    if (result.code === 200) {
      if (loading) {
        setFriends((prev) => [...prev, ...result.members]);
      } else {
        setFriends(result.members);
      }

      setIsLoading(false);
      setIsLoadingMore(false);
      setLoadMorePath(result?.load_more_url);
      setReciveCount(result?.receivedRequestCount);
      setSendCount(result?.sentRequestCount);
      setHasMore(result?.pagination?.hasNextPage);
    } else {
      setIsLoading(false);
      setIsLoadingMore(false);
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleChangeTab = (event, newValue) => {
    setSearchText("");
    setSelectedFriend([]);
    setTabValue(newValue);
  };

  const loadMoreData = () => {
    setIsLoadingMore(true);
    get_friends_list(loadMorePath, searchText, true);
  };

  const lastBookElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreData();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, hasMore, loadMorePath, observer]
  );

  const handlePartner = (value, type) => {
    if (
      type === "cancel_friend_request" ||
      type === "unfriend_partner_Request"
    ) {
      setFriends((prev) =>
        prev.map((item) =>
          item._id === value ? { ...item, request_status: "accepted" } : item
        )
      );
    }
  };

  const handleAddFriend = async (value, type, partner_value) => {
    let post_data = {
      receiver_id: value,
      type: type,
    };
    const result = await _api_add_friend(post_data);
    if (result.code === 200) {
      if (type === "unfriend_partner_Request") {
        enqueueSnackbar(
          "Accountability Partner has been removed successfully",
          {
            variant: "success",
          }
        );
      } else {
        enqueueSnackbar(result.message, { variant: "success" });
      }

      if (partner_value) {
        handlePartner(value, type);
      }

      if (type === "send_accountability_request") {
        setFriends((prev) =>
          prev.map((item) =>
            item._id === value
              ? { ...item, request_status: "pending_accountability_partner" }
              : item
          )
        );
      }

      if (type === "send_request") {
        setSendCount((prev) => prev + 1);
      } else if (type === "cancel_friend_request") {
        setSendCount((prev) => prev - 1);
      }

      if (
        type === "reject_request" ||
        type === "accept_accountability_request"
      ) {
        setReciveCount((prev) => prev - 1);
      }

      if (type === "send_request" || type === "accept_request") {
        setSelectedFriend((prev) => [...prev, value]);
      } else {
        setSelectedFriend((prev) => prev.filter((friend) => friend !== value));
        if (
          tabValue === 2 ||
          type === "reject_request" ||
          type === "unfriendRequest" ||
          type === "accept_accountability_request" ||
          (type === "unfriend_partner_Request" && !partner_value)
        ) {
          setFriends((prev) => prev.filter((friend) => friend._id !== value));
        }
      }
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleSearchText = (event) => {
    const value = event.target.value;
    const sanitizedValue = value.replace(/[^a-zA-Z0-9\s]/g, "");
    setSearchText(sanitizedValue);
  };

  const tab_data_object = {
    friends: friends,
    handleAddFriend: handleAddFriend,
    hasMore: hasMore,
    isLoadingMore: isLoadingMore,
    lastBookElementRef: lastBookElementRef,
    loadMoreData: loadMoreData,
    selectedFriend: selectedFriend,
    partnerList: partnerList,
  };

  const TABS_OPTIONS = [
    {
      title: "Friends List",
      component: isLoading ? (
        loader
      ) : (
        <GymMembers {...tab_data_object} type="friends_list" />
      ),
    },
    {
      title: "GYMRITE Members",
      component: isLoading ? (
        loader
      ) : (
        <GymMembers {...tab_data_object} type="gym_rite" />
      ),
    },
    {
      title: "Sent Requests",
      count: sendCount,
      component: isLoading ? (
        loader
      ) : (
        <GymMembers {...tab_data_object} type="sent_request" />
      ),
    },
    {
      title: "Received Requests",
      count: reciveCount,
      component: isLoading ? (
        loader
      ) : (
        <GymMembers {...tab_data_object} type="received_request" />
      ),
    },
    {
      title: "Accountability Partners",
      component: isLoading ? (
        loader
      ) : (
        <GymMembers {...tab_data_object} type="accountability_partner_list" />
      ),
    },
  ];

  useEffect(() => {
    if (!isLoading) {
      const timeoutId = setTimeout(() => {
        get_friends_list(loadMorePath, searchText);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchText]);

  useEffect(() => {
    get_friends_list(load_More_Path, searchText);
  }, [tabValue]);

  return (
    <GrPage
      sx={{
        pb: gr.space[8],
      }}
    >
      <GrPageHeader
        onBack={() => navigate(`/chat`)}
        backLabel="Back to messages"
        title="Friends"
        sx={{ mb: gr.space[4] }}
      />
      {/* Toolbar: tabs on the left, search on the right (on phones the search goes above the tabs). */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: gr.space[4],
          mb: gr.space[6],
          // One divider under the whole toolbar (tabs + search), not only under the tabs.
          borderBottom: `1px solid ${gr.color.line}`,
          "@media (max-width: 599.95px)": {
            flexDirection: "column-reverse",
            alignItems: "stretch",
          },
        }}
      >
        <GrTabs
          value={tabValue}
          onChange={handleChangeTab}
          ariaLabel="Friends"
          tabs={TABS_OPTIONS.map((item, index) => ({
            value: index,
            label: item.title,
            count: item.count || undefined,
            id: `friends-tab-${index}`,
            "aria-controls": `friends-tabpanel-${index}`,
          }))}
          sx={{ flex: 1, minWidth: 0, borderBottom: 0 }}
        />
        <GrSearch
          value={searchText}
          onChange={handleSearchText}
          onClear={() => setSearchText("")}
          placeholder="Search"
          label="Search people"
          sx={{ width: { xs: "100%", sm: 260 }, flex: "none" }}
        />
      </Box>
      <Box
        role="tabpanel"
        id={`friends-tabpanel-${tabValue}`}
        aria-labelledby={`friends-tab-${tabValue}`}
      >
        {TABS_OPTIONS[tabValue]?.component}
      </Box>
    </GrPage>
  );
}

export default FriendList;
